import apiClient, { handleApiResponse, handleApiError } from './api';
import type { ApiResponse, Agency, ProfileUpdateData, DashboardStats } from '../types';

class AgencyService {
  /**
   * Get agency profile
   */
  async getProfile(): Promise<ApiResponse<Agency>> {
    try {
      const response = await apiClient.get('/agency/profile');
      const result = handleApiResponse(response);

      // Backend returns data at response.data.agency, not response.data
      if (result.success && result.data && (result.data as any).agency) {
        return {
          success: true,
          message: result.message,
          data: (result.data as any).agency // Extract agency from nested structure
        };
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update agency profile
   */
  async updateProfile(data: ProfileUpdateData): Promise<ApiResponse<Agency>> {
    try {
      const response = await apiClient.put('/agency/profile', data);
      const result = handleApiResponse(response);

      // Backend returns data at response.data.agency
      if (result.success && result.data && (result.data as any).agency) {
        const agency = (result.data as any).agency;

        // Update localStorage with new data
        localStorage.setItem('agency_user', JSON.stringify(agency));

        return {
          success: true,
          message: result.message,
          data: agency
        };
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Get dashboard stats
   */
  async getDashboard(): Promise<ApiResponse<DashboardStats>> {
    try {
      const response = await apiClient.get<ApiResponse<DashboardStats>>('/agency/dashboard');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Update business open/closed status
   */
  async updateBusinessStatus(isOpenForBusiness: boolean): Promise<ApiResponse<any>> {
    try {
      const response = await apiClient.patch('/agency/business-status', { isOpenForBusiness });
      const result = handleApiResponse(response);

      // Update localStorage with new status
      const storedUser = localStorage.getItem('agency_user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        user.isOpenForBusiness = isOpenForBusiness;
        localStorage.setItem('agency_user', JSON.stringify(user));
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  /**
   * Accept agency terms and persist acknowledgement on the server
   */
  async acceptTerms(): Promise<ApiResponse<Agency>> {
    try {
      const response = await apiClient.post('/agency/terms/accept');
      const result = handleApiResponse(response);

      if (result.success && result.data && (result.data as any).agency) {
        const agency = (result.data as any).agency as Agency;
        localStorage.setItem('agency_user', JSON.stringify(agency));
        return {
          success: true,
          message: result.message,
          data: agency,
        };
      }

      return result;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }
}

const agencyServiceInstance = new AgencyService();
export default agencyServiceInstance;